import type { BirthVerse } from '../types/verses';

const CARD_WIDTH = 1080;
const TEXT_WIDTH = 820;

/** Wrap every word, including unbroken strings, without clipping verse text. */
export function wrapCardText(
  context: Pick<CanvasRenderingContext2D, 'measureText'>,
  text: string,
  maxWidth: number
): string[] {
  const lines: string[] = [];
  let line = '';

  for (const word of text.trim().split(/\s+/)) {
    const candidate = line ? `${line} ${word}` : word;
    if (context.measureText(candidate).width <= maxWidth) {
      line = candidate;
      continue;
    }
    if (line) lines.push(line);
    line = '';
    for (const character of word) {
      if (line && context.measureText(line + character).width > maxWidth) {
        lines.push(line);
        line = '';
      }
      line += character;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** Create an original typographic keepsake locally; no remote images or date uploads. */
export async function createVerseCardImage(verse: BirthVerse, date: Date): Promise<Blob> {
  if (document.fonts?.ready) await document.fonts.ready;

  const canvas = document.createElement('canvas');
  canvas.width = CARD_WIDTH;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Your browser could not create a verse card.');

  let fontSize = 56;
  context.font = `${fontSize}px Georgia, serif`;
  let lines = wrapCardText(context, verse.text, TEXT_WIDTH);
  while (lines.length * fontSize * 1.45 > 700 && fontSize > 44) {
    fontSize -= 2;
    context.font = `${fontSize}px Georgia, serif`;
    lines = wrapCardText(context, verse.text, TEXT_WIDTH);
  }
  const lineHeight = fontSize * 1.45;
  const verseTop = 465;
  const referenceY = verseTop + lines.length * lineHeight + 62;
  canvas.height = Math.max(1350, Math.ceil(referenceY + 240));

  context.fillStyle = '#FAF7F2';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.strokeStyle = '#DED7CC';
  context.lineWidth = 2;
  context.strokeRect(42, 42, canvas.width - 84, canvas.height - 84);

  context.fillStyle = '#5145A0';
  context.fillRect(42, 42, 8, canvas.height - 84);
  context.textAlign = 'center';
  context.textBaseline = 'top';

  context.fillStyle = '#5145A0';
  context.font = 'bold 34px Arial, sans-serif';
  context.fillText('BirthVerse', CARD_WIDTH / 2, 100);
  context.fillStyle = '#786E66';
  context.font = '25px Arial, sans-serif';
  context.fillText('A VERSE FOR YOUR BIRTHDAY', CARD_WIDTH / 2, 170);

  const birthday = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  context.fillStyle = '#2E2840';
  context.font = '64px Georgia, serif';
  context.fillText(birthday, CARD_WIDTH / 2, 252);

  context.strokeStyle = '#BCA573';
  context.beginPath();
  context.moveTo(450, 369);
  context.lineTo(630, 369);
  context.stroke();

  context.fillStyle = '#5145A0';
  context.font = '94px Georgia, serif';
  context.fillText('“', CARD_WIDTH / 2, 380);
  context.fillStyle = '#322D3C';
  context.font = `${fontSize}px Georgia, serif`;
  lines.forEach((line, index) => context.fillText(line, CARD_WIDTH / 2, verseTop + index * lineHeight));

  context.fillStyle = '#5145A0';
  context.font = 'bold 34px Arial, sans-serif';
  context.fillText(verse.reference, CARD_WIDTH / 2, referenceY);
  context.fillStyle = '#786E66';
  context.font = '26px Arial, sans-serif';
  context.fillText('Find a verse for someone you love', CARD_WIDTH / 2, canvas.height - 170);
  context.font = 'bold 26px Arial, sans-serif';
  context.fillText('birthverse.com', CARD_WIDTH / 2, canvas.height - 122);

  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob);
      else reject(new Error('Your browser could not export the verse card.'));
    }, 'image/png');
  });
}

export async function downloadVerseCard(verse: BirthVerse, date: Date): Promise<void> {
  const image = await createVerseCardImage(verse, date);
  const objectUrl = URL.createObjectURL(image);
  const link = document.createElement('a');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  link.href = objectUrl;
  link.download = `birthverse-${month}-${day}.png`;
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
    // Keep the blob available until the browser has started reading the download.
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
}
